import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetIpdBillCharges, StoreIpdBillCharge } from "../../../../api/ipd/ipdBillChargesAPI";
import { GetLastPatientDischarge, StorePatientDischarge } from "../../../../api/ipd/patientDischargeAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";
import { GetLastEntry } from "../../../../api/common/common";

// Validation schema (basic example — you can expand this)
const schema = yup.object().shape({
    ipdEntryId: yup.string().required("IPD Entry is required"),
    billNo: yup.string().required("Bill No is required"),
    ipdNo: yup.string().required("IPD No is required"),
    dischargeDate: yup.string().required("Discharge Date is required"),
    dischargeTime: yup.string().required("Discharge Time is required"),
    noOfDays: yup.string().required("No of Days is required"),
    patientStatus: yup.string().required("Patient Status is required"),
    netAmount: yup
        .number()
        .typeError("Net Amount must be a number")
        .required("Net Amount is required"),
    totalCharges: yup
        .number()
        .typeError("Total Charges must be a number")
        .required("Total Charges is required"),
    toPay: yup
        .number()
        .typeError("To Pay must be a number")
        .required("To Pay is required"),
})

const AddPatientDischargeEntry = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [ipdEntries, setIpdEntries] = useState([]);
    const [billingCharges, setBillingCharges] = useState([]);
    const [patientDetails, setPatientDetails] = useState({});

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        control,
        watch,
        formState: { errors } } = useForm({
            resolver: yupResolver(schema),
            defaultValues: {
            }
        });

    const fetchIpdEntries = async () => {
        try {
            const response = await GetIpdEntries();
            if (response.success) {
                setIpdEntries(response.data);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    const handleChangeIpdEntry = (e) => {
        setValue("ipdEntryId", e.target.value);
        fetchIpdBillCharges({ ipdEntryId: e.target.value });
        setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
        setValue('advance', ipdEntries.find(ipd => ipd._id === e.target.value)?.advance || 0);
        const admitDate = ipdEntries.find(ipd => ipd._id === e.target.value)?.date;
        const noOfDays = new Date().getTime() - new Date(admitDate).getTime();
        setValue('noOfDays', (noOfDays / (1000 * 60 * 60 * 24)).toFixed(0));
        setValue('ipdNo', ipdEntries.find(ipd => ipd._id === e.target.value)?.ipdNo);
        setValue('dischargeDate', new Date().toISOString().split("T")[0]);
        setValue('dischargeTime', new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }));
    };

    const fetchIpdBillCharges = async (data) => {
        try {
            const response = await GetIpdBillCharges(data);
            if (response.success) {
                setBillingCharges(
                    response.data.flatMap((record) =>
                        record.billingCharges.map((item) => ({
                            date: item?.date,
                            billingChargesName: item?.billingChargesId?.name,
                            billingChargesGroupName: item?.billingChargesId?.billingChargesGroupId?.name,
                            doctorName: item?.doctorId?.name,
                            unit: item?.unit,
                            rate: item?.rate,
                            amount: item?.amount,
                            discount: item?.discount,
                            vNo: item?.vNo
                        }))
                    )
                );

                const totalCharges = response.data.reduce(
                    (sum, record) => sum + record.totalAmount,
                    0
                );
                const advance = patientDetails?.patientId?.advance || 0;
                const totalDiscount = response.data.reduce(
                    (sum, record) => sum + record.discountAmount,
                    0
                )
                const totalDiscountPercentage = totalDiscount / totalCharges * 100;
                setValue("netAmount", totalCharges, { shouldDirty: false });
                setValue("totalCharges", totalCharges, { shouldDirty: false });
                setValue("discountAmount", totalDiscount, { shouldDirty: false });
                setValue("discountPercentage", totalDiscountPercentage, { shouldDirty: false });
                setValue("toPay", totalCharges - advance - totalDiscount, { shouldDirty: false });

            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    const fetchLastEntry = async () => {
		try {
			const response = await GetLastEntry();
			if (response.success) {
                setValue("billNo", response?.data?.lastPatientDischargeEntry?.billNo ? response?.data?.lastPatientDischargeEntry?.billNo + 1 : 1);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

    useEffect(() => {
        fetchIpdEntries();
        fetchLastEntry();
    }, []);

    const calculateAll = () => {

        // 2. total Amount
        const total = totalCharges;
        if (getValues("totalAmount") !== total) {
            setValue("totalAmount", total, { shouldDirty: false });
        }
        // 3. Discount
        const discountPercent = parseFloat(getValues("discountPercentage")) || 0;

        if (discountPercent > 0) {
            const discountAmount = (total * discountPercent) / 100;

            if (getValues("discountAmount") !== discountAmount) {
                setValue("discountAmount", discountAmount.toFixed(2), { shouldDirty: false });
            }
        }

        const finalDiscount = parseFloat(getValues("discountAmount")) || 0;

        const toPayDiscount = total - finalDiscount;

        if (getValues("toPay") !== toPayDiscount) {
            setValue("toPay", toPayDiscount.toFixed(2), { shouldDirty: false });
        }
    };

    const discountPercentage = watch("discountPercentage");
    const discountAmount = watch("discountAmount"); // only if user edits manually
    const totalCharges = watch("totalCharges");

    useEffect(() => {
        calculateAll();
    }, [discountPercentage, discountAmount]);


    const onSubmit = async data => {
        try {
            dispatch({ type: "LOADER_START" });
            data.billingCharges = billingCharges;
            const response = await StorePatientDischarge(data);

            if (response.success) {
                showToast("success", response.message);
                navigate(`/ipd/transaction/patient-discharge-entry/list`);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    const formRef = useRef(null);
    useKeyboardSubmit(handleSubmit, onSubmit);
    useEnterNavigation(formRef);

    return (
        <Fragment>
            <Breadcrumb parent="Patient Discharge" title="Add Patient Discharge Entry" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between">
                                <h5>Add Patient Discharge Entry</h5>
                                <Link
                                    to={`${process.env
                                        .PUBLIC_URL}/ipd/transaction/patient-discharge-entry/list`}
                                >
                                    <button className="btn btn-primary" id="list">
                                        List Patient Discharge Entry
                                    </button>
                                    <UncontrolledTooltip placement="top" target="list">
                                        List Patient Discharge Entry
                                    </UncontrolledTooltip>
                                </Link>
                            </div>

                            <form
                                ref={formRef}
                                className="form theme-form"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Room No</label>
                                                <select
                                                    className="form-control"
                                                    {...register("ipdEntryId", {
                                                        onChange: (e) => {
                                                            handleChangeIpdEntry(e);
                                                        }
                                                    })}
                                                >
                                                    <option value="">Select Ipd No</option>
                                                    {ipdEntries.map(ipd =>
                                                        <option key={ipd._id} value={ipd._id}>
                                                            {ipd.roomId?.roomNo}
                                                        </option>
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Bill No</label>
                                                <input
                                                    {...register("billNo")}
                                                    className="form-control"
                                                    placeholder="bill No"
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Ipd No</label>
                                                <input
                                                    {...register("ipdNo")}
                                                    className="form-control"
                                                    placeholder="ipd No"
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Discharge Date</label>
                                                <input
                                                    {...register("dischargeDate")}
                                                    type="date"
                                                    className="form-control"
                                                    placeholder="Discharge Date"
                                                />
                                                {errors.dischargeDate && (
                                                    <span className="text-danger">
                                                        {errors.dischargeDate.message}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Discharge Time</label>
                                                <input
                                                    {...register("dischargeTime")}
                                                    type="time"
                                                    className="form-control"
                                                    placeholder="Discharge Time"
                                                />
                                                {errors.dischargeTime && (
                                                    <span className="text-danger">
                                                        {errors.dischargeTime.message}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>No Of Days</label>
                                                <input
                                                    {...register("noOfDays")}
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="No Of Days"
                                                />
                                                {errors.noOfDays && (
                                                    <span className="text-danger">
                                                        {errors.noOfDays.message}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <h5 className="mt-3 mb-3 text-primary">
                                            Patient Details
                                        </h5>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>UHID No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="UHID No"
                                                    value={patientDetails?.patientId?.uhidNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit Date</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit Date"
                                                    value={patientDetails?.date?.split("T")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit time</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit time"
                                                    value={patientDetails?.date?.split("T")[1].split(".")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Ipd No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Ipd No"
                                                    value={patientDetails?.ipdNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Patient</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.patientName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Guardian Name</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Guardian Name"
                                                    value={patientDetails?.patientId?.guardianName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Age</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.age}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Case</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Case"
                                                    value={patientDetails?.caseId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Consultant Doctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Consultant Doctor"
                                                    value={patientDetails?.consultantDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>ReferenceDoctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="ReferenceDoctor"
                                                    value={patientDetails?.referenceDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Address</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Address"
                                                    value={patientDetails?.patientId?.address}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <h5 className="mt-3 mb-3 text-primary">
                                            Billing Charges
                                        </h5>
                                    </div>
                                    <div className="row">
                                        <div className="col-12 my-3">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Billing Group</th>
                                                        <th>Billing Charges</th>
                                                        <th>Doctor Name</th>
                                                        <th>Unit</th>
                                                        <th>Rate</th>
                                                        <th>Amount</th>
                                                        <th>Disocunt</th>
                                                        <th>V.No</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {billingCharges.map((field, index) => (
                                                        <tr key={field.id}>
                                                            <td>
                                                                {field.date}
                                                            </td>
                                                            <td>
                                                                {field.billingChargesGroupName}
                                                            </td>

                                                            <td>
                                                                {field.billingChargesName}
                                                            </td>

                                                            <td>
                                                                {field.doctorName}
                                                            </td>

                                                            <td>
                                                                {field.unit}
                                                            </td>

                                                            <td>
                                                                {field.rate}
                                                            </td>

                                                            <td>
                                                                {field.amount}
                                                            </td>

                                                            <td>
                                                                {field.discount}
                                                            </td>

                                                            <td>
                                                                {field.vNo}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div className="row">

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Patient Status</label>
                                                <select
                                                    className="form-control"
                                                    {...register("patientStatus")}
                                                    placeholder="Patient Status"
                                                >
                                                    <option value="Improved">Improved</option>
                                                    <option value="Recovered">Recovered</option>
                                                    <option value="Birth">Birth</option>
                                                    <option value="No Changed">No Changed</option>
                                                    <option value="Referred">Referred</option>
                                                    <option value="Expired">Expired</option>
                                                    <option value="LAMA">LAMA</option>
                                                    <option value="Self Discharge">Self Discharge</option>
                                                </select>
                                                {errors.patientStatus &&
                                                    <p className="text-danger">
                                                        {errors.patientStatus.message}
                                                    </p>}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Net Amount</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    {...register("netAmount")}
                                                    placeholder="Net Amount"
                                                    readOnly
                                                />
                                                {errors.netAmount &&
                                                    <p className="text-danger">
                                                        {errors.netAmount.message}
                                                    </p>}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Total Charges</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    {...register("totalCharges")}
                                                    placeholder="Total Charges"
                                                    readOnly
                                                />
                                                {errors.totalCharges &&
                                                    <p className="text-danger">
                                                        {errors.totalCharges.message}
                                                    </p>}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>ICDX</label>
                                                <input
                                                    className="form-control"
                                                    {...register("icdx")}
                                                    placeholder="ICDX"
                                                />
                                                {errors.icdx &&
                                                    <p className="text-danger">
                                                        {errors.icdx.message}
                                                    </p>}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Advance</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    {...register("advance")}
                                                    placeholder="Advance"
                                                    readOnly
                                                />
                                                {errors.advance &&
                                                    <p className="text-danger">
                                                        {errors.advance.message}
                                                    </p>}
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Discount Percentage</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="form-control"
                                                    {...register("discountPercentage", {
                                                        onChange: (e) => {
                                                            if (e.target.value > 100) {
                                                                e.target.value = 100;
                                                            }
                                                            const discountAmount = (e.target.value * totalCharges / 100).toFixed(2);
                                                            setValue("discountAmount", discountAmount);
                                                        }
                                                    })}
                                                    placeholder="Discount Percentage"
                                                />
                                                {errors.discountPercentage &&
                                                    <p className="text-danger">
                                                        {errors.discountPercentage.message}
                                                    </p>}
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Discount Amount</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="form-control"
                                                    {...register("discountAmount", {
                                                        onChange: (e) => {
                                                            const discountPercentage = (e.target.value * 100 / totalCharges).toFixed(2);
                                                            setValue("discountPercentage", discountPercentage);
                                                        }
                                                    })}
                                                    placeholder="Discount Amount"
                                                />
                                                {errors.discountAmount &&
                                                    <p className="text-danger">
                                                        {errors.discountAmount.message}
                                                    </p>}
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Discount By</label>
                                                <input
                                                    className="form-control"
                                                    {...register("discountBy")}
                                                    placeholder="Discount By"
                                                />
                                                {errors.discountBy &&
                                                    <p className="text-danger">
                                                        {errors.discountBy.message}
                                                    </p>}
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Payment Mode</label>
                                                <select
                                                    className="form-control"
                                                    {...register("paymentMode")}
                                                    placeholder="Payment Mode"
                                                >
                                                    <option value="">Select Payment Mode</option>
                                                    <option value="Cash">Cash</option>
                                                    <option value="Online">Online</option>
                                                </select>
                                                {errors.paymentMode &&
                                                    <p className="text-danger">
                                                        {errors.paymentMode.message}
                                                    </p>}
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>To Pay</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    {...register("toPay")}
                                                    placeholder="To Pay"
                                                    readOnly
                                                />
                                                {errors.toPay &&
                                                    <p className="text-danger">
                                                        {errors.toPay.message}
                                                    </p>}
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Debit Account</label>
                                                <input
                                                    className="form-control"
                                                    {...register("debitAccount")}
                                                    placeholder="Debit Account"
                                                />
                                                {errors.debitAccount &&
                                                    <p className="text-danger">
                                                        {errors.debitAccount.message}
                                                    </p>}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Other</label>
                                                <input
                                                    className="form-control"
                                                    {...register("other")}
                                                    placeholder="Other"
                                                />
                                                {errors.other &&
                                                    <p className="text-danger">
                                                        {errors.other.message}
                                                    </p>}
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label>Final Diagnosis</label>
                                                <textarea
                                                    className="form-control"
                                                    {...register("finalDiagnosis")}
                                                    placeholder="Final Diagnosis"
                                                />
                                                {errors.finalDiagnosis &&
                                                    <p className="text-danger">
                                                        {errors.finalDiagnosis.message}
                                                    </p>}
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label>Remark</label>
                                                <textarea
                                                    className="form-control"
                                                    {...register("remark")}
                                                    placeholder="Remark"
                                                />
                                                {errors.remark &&
                                                    <p className="text-danger">
                                                        {errors.remark.message}
                                                    </p>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card-footer text-end">
                                    <button className="btn btn-primary me-2" type="submit">
                                        Submit
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default AddPatientDischargeEntry;
